import { useMemo } from 'react'
import { get } from 'lodash'
import { Form, Question, SelectableCardGrid, Typography } from '@commutifi-fe/ui'
import { ModeCategory } from '@commutifi/modes'
import { QUESTION_TYPES } from '@commutifi-fe/interfaces'
import { CombinedModes } from '@commutifi-fe/constants'

import { AddressPoint, AddressPointWithTimezone, Route } from 'shared/interfaces/Commute'
import GoogleTransitDirections from 'shared/components/common/GoogleTransitDirections'
import { CommuteModePicker } from 'shared/components/common/CommuteModePicker'
import { primaryRoutePath, secondaryRouteObjectId } from 'constants/onboarding'
import { ComposedInputComponentProps } from 'shared/interfaces/Survey'
import { inferModeFromLegs, isValidCombinedMode } from 'utils/modes'
import { FormattedMessage, useIntl } from 'locales/index'
import { useDeepCompareEffect } from 'shared/hooks/global'
import { FormItemInjectedProps } from 'shared/interfaces'
import { generateSubQuestionId } from 'utils/helpers/surveys'
import { multiModesQuestion, primaryRouteQId, secondaryRouteQId, secondaryRouteQuestion } from './customQuestions'

const { ClickableCard } = SelectableCardGrid
const { Text } = Typography

type CommuteRouterValueProp = Record<string, { route: Route }> & Record<string, string>
export interface ICommuteRouter {
  startPoint: AddressPoint
  endPoint: AddressPointWithTimezone // (office building has a timezone)
  arrivalTime?: string
  // Boolean to indicate if we should display the question to ask the user
  // if he has a secondary commute
  askForSecondaryCommute?: boolean
  /**
   * See CommuteModePicker.tsx for more information
   */
  showDropdownOptionsCount?: boolean
}
type CommuteRouterProps = ComposedInputComponentProps<CommuteRouterValueProp> & ICommuteRouter

type SecondaryRouteProps = FormItemInjectedProps<string>

const onlyPrimary = 'onlyPrimary'
const hasSecondary = 'hasSecondary'

export const nextSubSection = (section: number, step = 0.1): number => Number((section + step).toFixed(1))

function HasSecondaryRoute({ id, onChange, value }: SecondaryRouteProps) {
  const handleOnChange = (value: string) => {
    if (onChange) {
      onChange(value)
    }
  }

  return (
    <SelectableCardGrid id={id} onChange={handleOnChange} initialSelectedCards={value ? [value] : []} baseWidth="auto">
      <ClickableCard
        id={onlyPrimary}
        title={<FormattedMessage id="commuteRoute.secondaryRoute.question.options.onlyOne" />}
      />
      <ClickableCard
        id={hasSecondary}
        title={<FormattedMessage id="commuteRoute.secondaryRoute.question.options.secondary" />}
      />
    </SelectableCardGrid>
  )
}

function CommuteRouter({
  id,
  question,
  value = {},
  startPoint,
  endPoint,
  arrivalTime,
  askForSecondaryCommute,
  form,
  upsertQuestions,
  validation,
  showDropdownOptionsCount
}: CommuteRouterProps) {
  const intl = useIntl()
  const { getFieldValue } = form
  // Should be something like commuteProfile_route.primary.route
  const routeSubQuestionId = useMemo(() => generateSubQuestionId(question, primaryRoutePath), [question])

  // Should be something like commuteProfile_route.secondary
  const secondaryRouteSubQuestionId = useMemo(() => generateSubQuestionId(question, secondaryRouteObjectId), [question])
  const primaryRoute: Route = getFieldValue(routeSubQuestionId) || get(value, primaryRoutePath)
  const isPublicTransitOnlyRoute = useMemo(
    () => primaryRoute && inferModeFromLegs(primaryRoute.legs).category === ModeCategory.PublicTransit,
    [primaryRoute]
  )
  const secondaryRoute = getFieldValue(secondaryRouteSubQuestionId) || get(value, secondaryRouteObjectId)

  /**
   * COMBINED MODES : EXTRA QUESTION IN THE NEXT SECTION
   *
   * The way it works, we need to send an action to the redux survey so we can add a question
   * to the next section (This is why we generate a new sub-section)
   *
   * We use sub-sections to avoid interfering with the original survey sections which are
   * integers, so by using sub-section (ex. 3.1) we won't cause any trouble
   *
   * Add question to know the details about the user commute steps (commute timeline component)
   * when the user has a combined modes commute
   */
  useDeepCompareEffect(() => {
    const routeFirstLeg = primaryRoute?.legs?.[0] || {}
    if (!routeFirstLeg.category) {
      return
    }
    const section = nextSubSection(question.section)
    // Add a question to the next section when the user answers he uses
    // mixed modes of drive & transit
    const newQuestion = multiModesQuestion({
      // Use preset drive and transit commute timeline
      ...(routeFirstLeg.category === CombinedModes.DriveAndTransit && {
        type: QUESTION_TYPES.commuteTimelineDriveAndTransit
      }),
      id: primaryRouteQId,
      conditionalQuestionId: question.id,
      section,
      enabled: isValidCombinedMode(routeFirstLeg.category),
      // Needs to be the same as the form field decorator id so we can
      // prefill the section form with already answered questions
      referenceKey: routeSubQuestionId,
      content: {
        title: intl.formatMessage({ id: 'commuteRoute.timeline.questions.primaryRoute' })
      }
    })
    upsertQuestions(newQuestion)
  }, [primaryRoute, routeSubQuestionId, question])

  /**
   * USER HAS A SECONDARY COMMUTE - EXTRA QUESTION IN THE NEXT SECTION
   *
   * Add secondary commute router question when the user says he
   * has a secondary route
   */
  useDeepCompareEffect(() => {
    if (!secondaryRoute) {
      return
    }

    const routeQuestion = secondaryRouteQuestion({
      id: secondaryRouteQId,
      referenceKey: secondaryRouteSubQuestionId,
      // Using +0.2 as a sub-step because we might have a previous sub-step
      // generated from the primary route
      section: nextSubSection(question.section, 0.2),
      conditionalQuestionId: question.id,
      enabled: secondaryRoute === hasSecondary,
      content: {
        title: intl.formatMessage({ id: 'commuteRoute.secondaryRoute.questions.modes' })
      }
    })
    upsertQuestions(routeQuestion)
  }, [secondaryRoute, question])

  return (
    <>
      <Form.Item
        name={routeSubQuestionId}
        {...validation}
        label={<Question title={get(question, 'content.title')} hint={get(question, 'content.hint')} />}
      >
        <CommuteModePicker
          id={id}
          startPoint={startPoint}
          endPoint={endPoint}
          showDropdownOptionsCount={showDropdownOptionsCount}
        />
      </Form.Item>
      {/* If the user public transit we want to overwrite the question answers with more details coming from google api */}
      <Form.Item
        hidden={!isPublicTransitOnlyRoute}
        name={routeSubQuestionId}
        valuePropName="transitValue"
        {...validation}
        label={
          <Question
            title={<FormattedMessage id="commuteRoute.modes.transit.question" />}
            hint={<FormattedMessage id="commuteRoute.modes.transit.question.hint" />}
          />
        }
        data-testid="public-transit-suggestions"
      >
        <GoogleTransitDirections.CommutifiTransitRoute
          id={id}
          enabled={Boolean(isPublicTransitOnlyRoute)}
          startLocation={startPoint?.location}
          startAddress={startPoint?.formattedAddress}
          endLocation={endPoint?.location}
          endAddress={endPoint?.formattedAddress}
          arrivalTime={arrivalTime}
          timezone={endPoint?.timezone}
        >
          <GoogleTransitDirections.OtherOption />
        </GoogleTransitDirections.CommutifiTransitRoute>
      </Form.Item>
      {askForSecondaryCommute ? (
        <Form.Item
          name={secondaryRouteSubQuestionId}
          {...validation}
          label={
            <Question
              title={<FormattedMessage id="commuteRoute.secondaryRoute.question" />}
              hint={<FormattedMessage id="commuteRoute.secondaryRoute.question.hint" />}
            />
          }
        >
          <HasSecondaryRoute id={id} />
          {showDropdownOptionsCount && (
            <Text type="secondary" className="u-margin-top--tiny" block>
              {intl.formatMessage({ id: 'surveys.questions.types.commuteRouter.secondaryRoute.description' })}
            </Text>
          )}
        </Form.Item>
      ) : null}
    </>
  )
}

export default CommuteRouter
